import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useCallback,
  useState,
  useRef,
  useMemo,
  type ReactNode,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { useWebSocket } from "./WebSocketContext";
import { NotificationContainer } from "../components/notifications/NotificationContainer";
import { WS_CHANNELS } from "@/utils/websocket";

// Notification types
export type NotificationType =
  | "success"
  | "error"
  | "info"
  | "warning"
  | "progress";

// Notification interface
export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number; // accepts seconds (<=60) or milliseconds (>60)
  progress?: number; // for progress notifications (0-100)
  data?: any; // Optional data that can be passed with the notification
  autoClose?: boolean; // Whether to automatically close the notification
  action?: {
    label: string;
    onClick: () => void;
  };
  createdAt: number; // timestamp
}

// Actions for the reducer
type NotificationAction =
  | {
      type: "ADD_NOTIFICATION";
      payload: Omit<Notification, "createdAt">;
    }
  | { type: "REMOVE_NOTIFICATION"; payload: { id: string } }
  | {
      type: "UPDATE_NOTIFICATION";
      payload: { id: string } & Partial<Notification>;
    }
  | { type: "CLEAR_ALL_NOTIFICATIONS" };

// Context type
export interface NotificationContextType {
  notifications: Notification[];
  addNotification: (
    notification: Omit<Notification, "id" | "createdAt">
  ) => string;
  removeNotification: (id: string) => void;
  updateNotification: (id: string, updates: Partial<Notification>) => void;
  clearAllNotifications: () => void;
  // Utility functions
  showSuccess: (
    title: string,
    message?: string,
    options?: Partial<Notification>
  ) => string;
  showError: (
    title: string,
    message?: string,
    options?: Partial<Notification>
  ) => string;
  showInfo: (
    title: string,
    message?: string,
    options?: Partial<Notification>
  ) => string;
  showWarning: (
    title: string,
    message?: string,
    options?: Partial<Notification>
  ) => string;
  showProgress: (
    title: string,
    progress?: number,
    options?: Partial<Notification>
  ) => string;
  // Websocket notification subscriptions
  subscribeToEvent: (
    eventType: string,
    handler: (data: any) => void
  ) => () => void;
}

// Create context
const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

// Default durations for different notification types
const DEFAULT_DURATIONS = {
  success: 5000,
  error: 8000,
  info: 5000,
  warning: 7000,
  progress: null, // Progress notifications don't auto-close by default
};

const normalizeDuration = (duration?: number): number | undefined => {
  if (duration === undefined) return duration;
  if (duration <= 0) return duration;
  if (duration <= 60) return duration * 1000;
  return duration;
};

// Reducer function to manage notifications
function notificationReducer(
  state: Notification[],
  action: NotificationAction
): Notification[] {
  switch (action.type) {
    case "ADD_NOTIFICATION": {
      const newNotification: Notification = {
        createdAt: Date.now(),
        duration: DEFAULT_DURATIONS[action.payload.type] ?? 5000,
        autoClose: action.payload.type !== "progress",
        ...action.payload,
      };
      newNotification.duration = normalizeDuration(newNotification.duration);
      return [...state, newNotification];
    }
    case "REMOVE_NOTIFICATION":
      return state.filter(
        (notification) => notification.id !== action.payload.id
      );
    case "UPDATE_NOTIFICATION":
      return state.map((notification) =>
        notification.id === action.payload.id
          ? { ...notification, ...action.payload }
          : notification
      );
    case "CLEAR_ALL_NOTIFICATIONS":
      return [];
    default:
      return state;
  }
}

// Provider component
export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [notifications, dispatch] = useReducer(notificationReducer, []);
  const [eventSubscriptions, setEventSubscriptions] = useState<
    Record<string, ((data: any) => void)[]>
  >({});

  // Call useWebSocket unconditionally (Rules of Hooks)
  // The hook itself should handle SSR gracefully by returning null/undefined
  const websocket = useWebSocket();

  // Add notification
  const addNotification = useCallback(
    (notification: Omit<Notification, "id" | "createdAt">) => {
      const id = uuidv4();
      const payload = { ...notification, id };

      dispatch({ type: "ADD_NOTIFICATION", payload });

      // Return the actual ID so callers can reference this notification later
      return id;
    },
    []
  );

  // Remove notification
  const removeNotification = useCallback((id: string) => {
    dispatch({ type: "REMOVE_NOTIFICATION", payload: { id } });
  }, []);

  // Update notification
  const updateNotification = useCallback(
    (id: string, updates: Partial<Notification>) => {
      dispatch({ type: "UPDATE_NOTIFICATION", payload: { id, ...updates } });
    },
    []
  );

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    dispatch({ type: "CLEAR_ALL_NOTIFICATIONS" });
  }, []);

  // Utility function to show success notification
  const showSuccess = useCallback(
    (title: string, message?: string, options?: Partial<Notification>) => {
      const id = uuidv4();
      const notification = {
        id,
        type: "success" as const,
        title,
        message,
        ...options,
      };

      dispatch({ type: "ADD_NOTIFICATION", payload: notification });
      return id;
    },
    []
  );

  // Utility function to show error notification
  const showError = useCallback(
    (title: string, message?: string, options?: Partial<Notification>) => {
      const id = uuidv4();
      const notification = {
        id,
        type: "error" as const,
        title,
        message,
        ...options,
      };

      dispatch({ type: "ADD_NOTIFICATION", payload: notification });
      return id;
    },
    []
  );

  // Utility function to show info notification
  const showInfo = useCallback(
    (title: string, message?: string, options?: Partial<Notification>) => {
      const id = uuidv4();
      const notification = {
        id,
        type: "info" as const,
        title,
        message,
        ...options,
      };

      dispatch({ type: "ADD_NOTIFICATION", payload: notification });
      return id;
    },
    []
  );

  // Utility function to show warning notification
  const showWarning = useCallback(
    (title: string, message?: string, options?: Partial<Notification>) => {
      const id = uuidv4();
      const notification = {
        id,
        type: "warning" as const,
        title,
        message,
        ...options,
      };

      dispatch({ type: "ADD_NOTIFICATION", payload: notification });
      return id;
    },
    []
  );

  // Utility function to show progress notification
  const showProgress = useCallback(
    (title: string, progress = 0, options?: Partial<Notification>) => {
      const id = uuidv4();
      const notification = {
        id,
        type: "progress" as const,
        title,
        progress,
        autoClose: false,
        ...options,
      };

      dispatch({ type: "ADD_NOTIFICATION", payload: notification });
      return id;
    },
    []
  );

  // Auto-close notifications when their duration is up
  useEffect(() => {
    const timers: Record<string, NodeJS.Timeout> = {};

    notifications.forEach((notification) => {
      if (notification.autoClose && notification.duration) {
        // Clear any existing timers for this notification
        if (timers[notification.id]) {
          clearTimeout(timers[notification.id]);
        }

        // Set new timer
        timers[notification.id] = setTimeout(() => {
          removeNotification(notification.id);
        }, notification.duration);
      }
    });

    // Clean up timers on unmount
    return () => {
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, [notifications, removeNotification]);

  // Create a ref to track processed message IDs
  // Handle websocket events per channel to avoid missing messages when different parts update the map
  useEffect(() => {
    if (!websocket) return;

    const unsubscribeFns: Array<() => void> = [];

    const channels: string[] = Object.values(WS_CHANNELS || {});
    channels.forEach((ch) => {
      // Only attach listeners; do NOT implicitly subscribe/unsubscribe here
      const off = websocket.onMessage(ch, (message: any) => {
        if (!message || !message.type) return;
        const handlers = eventSubscriptions[message.type];
        if (handlers && handlers.length > 0) {
          handlers.forEach((handler) => handler(message.payload || message));
        }
      });
      unsubscribeFns.push(off);
    });

    return () => {
      unsubscribeFns.forEach((u) => u());
    };
  }, [websocket, eventSubscriptions]);

  // Subscribe to websocket events
  const subscribeToEvent = useCallback(
    (eventType: string, handler: (data: any) => void) => {
      setEventSubscriptions((prev) => {
        const handlers = prev[eventType] || [];
        return {
          ...prev,
          [eventType]: [...handlers, handler],
        };
      });

      // Return unsubscribe function
      return () => {
        setEventSubscriptions((prev) => {
          const handlers = prev[eventType] || [];
          return {
            ...prev,
            [eventType]: handlers.filter((h) => h !== handler),
          };
        });
      };
    },
    []
  );

  const contextValue: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    updateNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showProgress,
    subscribeToEvent,
  };

  // Memoize the context value to prevent unnecessary re-renders
  const memoizedContextValue = useMemo(
    () => contextValue,
    [
      notifications,
      addNotification,
      removeNotification,
      updateNotification,
      clearAllNotifications,
      showSuccess,
      showError,
      showInfo,
      showWarning,
      showProgress,
      subscribeToEvent,
    ]
  );

  return (
    <NotificationContext.Provider value={memoizedContextValue}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
};

// Custom hook to use the notification context
export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

/**
 * Optional variant of useNotification that returns null if not inside a provider.
 * Useful for components that may or may not be rendered within a NotificationProvider.
 */
export const useNotificationOptional = (): NotificationContextType | null => {
  return useContext(NotificationContext) ?? null;
};
